import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../hooks/useLanguage';
import { content } from '../data/content';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';

const EMAILJS_SERVICE_ID  = 'service_d39zyzf';
const EMAILJS_TEMPLATE_ID = 'template_umqz52o';
const EMAILJS_PUBLIC_KEY  = '9FvvaDjcCbS1v_7Fi';

const INFO_ICONS = ['mail', 'phone', 'pin', 'clock'];

const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

const initial = { name: '', email: '', company: '', service: '', message: '' };

export const Contact = () => {
  const { language } = useLanguage();
  const t = content[language];
  const services = useMemo(
    () => [
      t.services.consulting.title,
      t.services.windowsServer.title,
      t.services.webDevelopment.title,
      t.services.windowsOS.title,
    ],
    [language] // eslint-disable-line
  );

  const [form, setForm] = useState(initial);
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success
  const [showError, setShowError] = useState(null); // 'required' | 'email' | null

  useEffect(() => {
    if (status !== 'success') return;
    const t = setTimeout(() => {
      setStatus('idle');
      setForm(initial);
      setTouched({});
    }, 6000);
    return () => clearTimeout(t);
  }, [status]);

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.email.trim() || !validateEmail(form.email)) e.email = !validateEmail(form.email) && form.email.trim() ? 'email' : true;
    if (!form.message.trim()) e.message = true;
    return e;
  }, [form]);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (showError) setShowError(null);
  };
  const onBlur = (e) => setTouched((tt) => ({ ...tt, [e.target.name]: true }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (errors.name || errors.message || errors.email === true) {
      setShowError('required');
      setTouched({ name: true, email: true, message: true, service: true, company: true });
      return;
    }
    if (errors.email === 'email') {
      setShowError('email');
      return;
    }
    setStatus('submitting');
    emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      {
        from_name:    form.name,
        from_email:   form.email,
        company:      form.company,
        service:      form.service,
        message:      form.message,
      },
      EMAILJS_PUBLIC_KEY
    ).then(() => {
      setStatus('success');
    }).catch(() => {
      setStatus('idle');
      setShowError('required');
    });
  };

  const f = t.contact.form;
  const showInvalid = (key) => touched[key] && errors[key];

  return (
    <Section
      id="contact"
      eyebrow={t.contact.eyebrow}
      title={t.contact.title}
      subtitle={t.contact.subtitle}
      align="center"
      className="bg-elev"
    >
      <div className="grid lg:grid-cols-12 gap-6 items-start">
        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={onSubmit}
            noValidate
            className="rounded-3xl border bg-surface p-6 sm:p-8"
            style={{ borderColor: 'var(--border)' }}
          >
            <AnimatePresence>
              {status === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  className="mb-5 rounded-2xl border bg-elev p-4 flex items-start gap-3"
                  style={{ borderColor: 'var(--mint)' }}
                >
                  <span
                    className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white shrink-0"
                    style={{ background: 'var(--mint)' }}
                  >
                    <Icon name="check" size={14} />
                  </span>
                  <p className="text-sm text-app">{f.success}</p>
                </motion.div>
              )}
              {showError && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  className="mb-5 rounded-2xl border bg-elev p-4 flex items-start gap-3"
                  style={{ borderColor: 'var(--coral)' }}
                  role="alert"
                >
                  <span
                    className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white shrink-0"
                    style={{ background: 'var(--coral)' }}
                  >
                    <Icon name="close" size={14} />
                  </span>
                  <p className="text-sm text-app">
                    {showError === 'email' ? f.errorEmail : f.errorRequired}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-xs font-medium text-muted mb-1.5">
                  {f.name}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  onBlur={onBlur}
                  required
                  aria-invalid={Boolean(showInvalid('name'))}
                  className="field"
                  placeholder="Ada Lovelace"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-muted mb-1.5">
                  {f.email}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  onBlur={onBlur}
                  required
                  aria-invalid={Boolean(showInvalid('email'))}
                  className="field"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-xs font-medium text-muted mb-1.5">
                  {f.company}
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={onChange}
                  onBlur={onBlur}
                  className="field"
                  placeholder="Acme GmbH"
                />
              </div>
              <div>
                <label htmlFor="service" className="block text-xs font-medium text-muted mb-1.5">
                  {f.service}
                </label>
                <select
                  id="service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  onBlur={onBlur}
                  className="field appearance-none pr-10"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.6'><path d='m6 9 6 6 6-6'/></svg>\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.9rem center',
                    backgroundSize: '1rem',
                  }}
                >
                  <option value="">—</option>
                  {services.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="message" className="block text-xs font-medium text-muted mb-1.5">
                {f.message}
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                onBlur={onBlur}
                required
                aria-invalid={Boolean(showInvalid('message'))}
                className="field resize-none"
                placeholder="…"
              />
              <p className="mt-2 text-xs text-subtle">{f.agreement}</p>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
              <div className="flex items-center gap-2 text-xs text-muted">
                <Icon name="lock" size={14} />
                <span>
                  {language === 'en'
                    ? 'Encrypted in transit. Stored in the EU.'
                    : 'Verschlüsselt übertragen. Speicherung in der EU.'}
                </span>
              </div>
              <Button
                as="button"
                type="submit"
                disabled={status === 'submitting'}
                icon={<Icon name={status === 'submitting' ? 'sparkle' : 'arrowRight'} size={14} />}
              >
                {status === 'submitting' ? f.sending : f.submit}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Info side */}
        <div className="lg:col-span-5 grid gap-4">
          {t.contact.info.map((row, i) => (
            <motion.div
              key={row.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.4, delay: 0.06 * i }}
              className="flex items-start gap-4 rounded-3xl border bg-surface p-5 lift"
              style={{ borderColor: 'var(--border)' }}
            >
              <span
                className="inline-flex items-center justify-center h-11 w-11 rounded-2xl text-white shrink-0"
                style={{
                  background:
                    i % 2 === 0
                      ? 'linear-gradient(135deg, var(--brand), var(--accent))'
                      : 'linear-gradient(135deg, var(--accent), var(--mint))',
                }}
              >
                <Icon name={INFO_ICONS[i]} size={18} />
              </span>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-subtle">{row.label}</p>
                {row.href ? (
                  <a href={row.href} className="text-sm text-app font-medium hover:underline break-words">
                    {row.value}
                  </a>
                ) : (
                  <p className="text-sm text-app font-medium break-words">{row.value}</p>
                )}
              </div>
            </motion.div>
          ))}

          <div
            className="rounded-3xl border p-5 bg-elev flex items-center gap-3"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="inline-flex h-2 w-2 rounded-full bg-signal-mint animate-pulse" />
            <p className="text-xs text-muted">
              {language === 'en'
                ? 'A senior consultant — not a salesperson — answers your enquiry.'
                : 'Ein Senior Consultant — kein Vertrieb — antwortet auf Ihre Anfrage.'}
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Contact;
